import { Component, ElementRef, EventEmitter, inject, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { DataViewer } from '../../../abstract/DataViewer';
import { MainComponent } from '../../../pages/main/main.component';
import { BeNeo4jService } from '../../../services/be-neo4j.service';
import { SharedService } from '../../../services/shared.service';
import { ShapeBounding } from '../../../clases/ShapeBounding';
import { TimelineComponent } from '../../timeline/timeline.component';
import { ServerResponse, ServerResponseObject } from '../../../interfaces/ServerResponse';
import { BoxMetrics } from '../../../interfaces/BoxMetrix';
import { assignHeatMap, assignMetrics, toggleHeatmapList } from '../../../utils/MetricsUtil';
import { GlobalMetricsComponent } from '../../global-metrics/global-metrics.component';
import { CanvasRenderer } from '../../../abstract/CanvasRenderer';
import { NotifyDialogService } from '../../../services/notify-dialog.service';
import { AllMetricsTypes, MetricsMeasures, MetricType } from '../../../interfaces/MetricType';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {MatSlideToggleChange, MatSlideToggleModule} from '@angular/material/slide-toggle';
import { CustomerDocument } from '../../../interfaces/CustomerDocument';
import { MatListModule } from '@angular/material/list';
import { DatePipe } from '../../../pipes/date-en-us.pipe';
import { TimeUnit } from '../../../enums/TimeUnit';
import { ComposedMetrics } from '../../../clases/ComposedMetrics';
import { NumOfDocs } from '../../../interfaces/NumOfDocs';
import { GraphItem } from '../../../interfaces/GraphItem';
import { HeaderValues } from '../../../interfaces/HeaderValues';
import { GraphDialogService } from '../../../services/graph-dialog.service';
import { map, Observable } from 'rxjs';
import { CenterCanvasComponent } from '../../buttons/center-canvas/center-canvas.component';
import { HeatMap } from '../../../interfaces/HeatMap';
import { ERROR } from '../../../shared/strings';
import { SimulationReturnValues } from '../../../interfaces/Simulation';
import { MatDialog } from '@angular/material/dialog';
import { SimulationGraphsComponent } from '../../dialogs/simulation-graphs/simulation-graphs.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataViewActionsComponent } from '../data-view-actions/data-view-actions.component';

@Component({
  selector: 'data-viewer',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    TimelineComponent,
    GlobalMetricsComponent,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    DatePipe,
    CenterCanvasComponent,
    MatSlideToggleModule,
    MatTooltipModule,
    DataViewActionsComponent
  ],
  providers: [{ provide: DataViewer, useExisting: DataViewerComponent }, GraphDialogService],
  templateUrl: './data-viewer.component.html',
  styleUrl: './data-viewer.component.scss'
})
export class DataViewerComponent extends CanvasRenderer {

  constructor(
    protected override beNeo4jService: BeNeo4jService,
    protected override sharedService: SharedService,
    private notifyDialogService: NotifyDialogService,
    private graphDialogService: GraphDialogService
  ){super(beNeo4jService, sharedService);}

  ERROR = ERROR;

  DataViewer: DataViewerComponent = this;

  @ViewChild('canvas', { static: true })
  set canvasRef(value: ElementRef<HTMLCanvasElement>) {
    let canvas : HTMLCanvasElement = value.nativeElement;
    this.canvas = canvas;
    this._canvasBounding = new ShapeBounding(0, 0, canvas.width, canvas.height);
  }

  @Input()
  override set appComponent(value: MainComponent) {
    this._appComponent = value;
  }

  override get appComponent(): MainComponent {
    return this._appComponent;
  }

  @Input()
  set containerElement(value: HTMLElement) {
    this._containerElement = value;
  }

  get containerElement(): HTMLElement {
    return this._containerElement;
  }

  @ViewChild('timeline') timeline!: TimelineComponent;
  // @ViewChild('simOverview') simOverview!: SimulationOverviewComponent;

  timeValues: { name: string, enum: TimeUnit}[] = [
    { name: "Weeks", enum: TimeUnit.WEEKS },
    { name: "Days", enum: TimeUnit.DAY },
    { name: "Hours", enum: TimeUnit.HOUR },
    { name: "minutes", enum: TimeUnit.MINUTES }
  ];

  metrics: BoxMetrics[] = [];
  metricsTypes!: MetricType[];
  currentMetricType!: MetricType;
  currentDate: Date = new Date();
  searchBox = new FormControl("");
  currentDocuments: CustomerDocument[] = [];
  userDocument!: CustomerDocument | null;
  isSearchingDocuments: boolean = false;
  currentMeasureUnit!: MetricsMeasures;
  currentTimeUnit: TimeUnit = TimeUnit.HOUR;
  composedMetrics: ComposedMetrics = new ComposedMetrics(this.currentMeasureUnit, this.currentTimeUnit);
  currentHeaderValues: HeaderValues[] = [];
  allGraphMetrics!: MetricType[];
  currentGraphMetric!: MetricType;

  showSearch: boolean = false;

  alertDocuments: number = 0;

  dialog = inject(MatDialog);

  currentSimulationHeaderValues: HeaderValues[] = [];
  simulationReturnValues: SimulationReturnValues | undefined;
  setSimulationValues(values: SimulationReturnValues) {
    this.currentSimulationHeaderValues = [
      { position: 0, label:"Avg Cycle Time", value: values.CYCLE_TIME.avg_cycle_time } as HeaderValues,
      { position: 1, label:"Max Cycle Time", value: values.CYCLE_TIME.max_cycle_time } as HeaderValues,
      { position: 2, label:"Min Cycle Time", value: values.CYCLE_TIME.min_cycle_time } as HeaderValues,
      { position: 3, label:"Customers Finished", value: values.CYCLE_TIME.customers_finished } as HeaderValues,
      { position: 3, label:"Primary Bottleneck", value: values.BOTTLENECK[0].task_name } as HeaderValues,
      { position: 3, label:"Secondary Bottleneck", value: values.BOTTLENECK[1].task_name } as HeaderValues,
      { position: 3, label:"Tertiary Bottleneck", value: values.BOTTLENECK[2].task_name } as HeaderValues
    ];
    
    this.simulationReturnValues = values;
  }

  showSimulationGraph() {
    const dialogRef = this.dialog.open(SimulationGraphsComponent, {
      data: this.simulationReturnValues!.GRAPHS,
      panelClass: 'full-dialog',
    });
    // dialogRef.afterClosed();
  }
  
  ngAfterViewInit(): void {
    this._isViewEditable = false;
    setTimeout(()=>{
      this.setupCanvas();
      this.bindEvents();
    }, 0)

    document.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });

    // this.simOverview.overviewToggle.subscribe((value: boolean)=>{
    //   this.onToggleMetrics(this.canvasItems, !value);
    //   this.draw();
    // });
  }

  // onToggleMetrics(shapes: AbstractShape[], value: boolean){
  //   for (const shape of shapes) {
  //     shape.setShowMetrics(value);
  //     if(shape.children.length > 0) this.onToggleMetrics(shape.children, value);
  //   }
  // }

  override onMouseMove(e: MouseEvent) {
    this.isShapeHover = this.canvasHelper.onHoverShape(e, this.canvasItems, this.hoveredShape);
    this.isConnectionHover = this.canvasHelper.onHoverConnection(e, this.connections, this.hoveredConnection);
    this.canvasHelper.paneCamera(e);
    this.draw();
  }


  override onMouseUp() {
    this.checkFocus();
    this.canvasHelper.stopCameraPanning();
    this.draw();
  }

  onChangeMetric(event: MatSelectChange){
    this.currentMetricType = event.value;
    if(this.userDocument){
      this.onSelectDocument(this.userDocument, this.currentDate.toISOString(), false);
    }else{
      this.getMetrics(this.currentDate);
    }
  }

  onChangeMeasureUnit(event: MatSelectChange){
    this.currentMeasureUnit = event.value;
    this.composedMetrics.measure = this.currentMeasureUnit;
    this.draw();
  }

  onChangeTimeUnit(event: MatSelectChange){
    this.currentTimeUnit = event.value
    this.composedMetrics.timeUnit = this.currentTimeUnit;
    this.draw();
  }

  heatMapToggle(event: MatSlideToggleChange){
    toggleHeatmapList(this.canvasItems, event.checked);
    this.draw();
  }

  getHeatMap(){
    let sub = this.beNeo4jService.getValuesBottleneck(this.currentDate.getFullYear(), this.currentDate.getMonth()+1, 0, this.savedDocument.hierarchy, this.savedDocument.model_UUID).subscribe({
      next: (res: ServerResponse<HeatMap>)=>{
        let heatmap = res.result;
        if(heatmap.length>0){
          assignHeatMap(this.canvasItems, heatmap);
        }
        sub.unsubscribe();
      }
    });
  }

  onGetMetrics(date: Date){
    this.currentDate = date;
    this.getMetrics(date);
  }

  override getMetricTypes(){
    this.beNeo4jService.MetricsTypes(this.savedDocument.hierarchy, this.savedDocument.connection.dataService.data_service_id).subscribe({
        next: (response: ServerResponseObject<AllMetricsTypes>)=>{
          let metrics = response.result.metric;
          if(metrics.length>0){
            this.metricsTypes = metrics;
            this.currentMetricType = this.metricsTypes[0];
            this.currentMeasureUnit = this.currentMetricType.measures[0];
            this.composedMetrics.measure = this.currentMeasureUnit;
            this.allGraphMetrics = response.result.graph;
            this.currentGraphMetric = this.allGraphMetrics[0];
            this.getMetrics(new Date());
            this.getHeatMap();
            this.getHeaderValues();
          }
        },
        error: (err)=>{
          this.isLoading = false;
        }
    });
  }

  getMetrics(date: Date){
    this.isLoading = true;
    this.beNeo4jService.Metrics(date.getFullYear(), date.getMonth()+1, date.getDate(), this.savedDocument.model_UUID, this.currentMetricType.metric_uuid, this.savedDocument.connection.dataService.data_service_id).subscribe({
        next: (response: ServerResponse<BoxMetrics>)=>{
          this.metrics = response.result;
          assignMetrics(this.canvasItems, this.metrics, this.composedMetrics);
          this.onGetWorstCases();
          this.draw();
          this.isLoading = false;
          this.getHeaderValues();
        },
        error: (err)=>{
          this.isLoading = false;
        }
    });
  }

  onTimeline(date: Date){
    if(this.currentDate.getMonth()+1 != date.getMonth()+1){
      this.getHeatMap();
    }
    this.currentDate = date;
    if(this.userDocument){
      this.onSelectDocument(this.userDocument, date.toISOString());
      return;
    }
    this.getMetrics(date);
  }

  onToggleSearch(){
    this.showSearch = !this.showSearch;
  }

  onSearchDocuments(){
    if(!this.searchBox.valid) return;
    let modelUUID = this.savedDocument.model_UUID;
    let hierarchy = this.savedDocument.hierarchy;
    this.isSearchingDocuments = true;
    this.beNeo4jService.GetDocumentsByReference(hierarchy, modelUUID, this.searchBox.value!).subscribe({
      next: (res: ServerResponse<CustomerDocument>)=>{
        let documents = res.result;
        this.currentDocuments = documents;
        this.draw();
        this.isSearchingDocuments = false;
      },
      error: (err)=>{
        this.isSearchingDocuments = false;
      }
    });
  }

  onSelectDocument(document: CustomerDocument, date: string, assignDate: boolean = false){
    this.userDocument = document;
    this.currentDate = new Date(document.dateTimeReferenceIdEnd);
    if(assignDate)this.timeline.setDate(this.currentDate);
    this.beNeo4jService.GetDocument(document.log__EDT_UUID__, date, this.currentMetricType.metric_uuid, this.savedDocument.connection.dataService.data_service_id).subscribe({
      next: (res: ServerResponse<BoxMetrics>)=>{
        let boxMetrics = res.result;
        this.metrics = boxMetrics;
        assignMetrics(this.canvasItems, this.metrics, this.composedMetrics, true);
        this.draw();
        this.showSearch = false;
      },
    });
  }

  onGetWorstCases(){
    this.beNeo4jService.getWorstCases(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth()+1,
      0,
      this.savedDocument.hierarchy,
      this.savedDocument.model_UUID,
      this.currentMetricType.metric_uuid,
      0,0,true,
      this.savedDocument.connection.dataService.data_service_id
    ).subscribe({
      next: (number: ServerResponse<CustomerDocument | NumOfDocs>)=>{
        this.alertDocuments = (number.result[0] as NumOfDocs).document;
      }
    });
  }

  getAffectedDocuments(){
    this.isSearchingDocuments = true;
    this.beNeo4jService.getWorstCases(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth()+1,
      0,
      this.savedDocument.hierarchy,
      this.savedDocument.model_UUID,
      this.currentMetricType.metric_uuid,
      0,100,false,
      this.savedDocument.connection.dataService.data_service_id
    ).subscribe({
      next: (res: ServerResponse<CustomerDocument | NumOfDocs>)=>{
        let documents = (res.result as CustomerDocument[]);
        this.currentDocuments = documents;
        this.draw();
        this.isSearchingDocuments = false;
      }
    });
  }
  
  getGraph = (showMetric: MetricType): Observable<GraphItem[]>=>{
    return this.beNeo4jService.getGraph(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth()+1,
      0,
      this.savedDocument.hierarchy,
      this.savedDocument.model_UUID,
      showMetric.metric_uuid,
      this.savedDocument.connection.dataService.data_service_id
    ).pipe(map((response: ServerResponse<GraphItem>)=>response.result))
  }

  showGraph(){
    this.graphDialogService.openDialog(this.allGraphMetrics, this.getGraph);
  }

  getHeaderValues(){
    this.beNeo4jService.getHeaderValues(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth()+1,
      this.currentDate.getDate(),
      this.savedDocument.hierarchy,
      this.savedDocument.model_UUID,
      this.currentMetricType.metric_uuid,
      this.savedDocument.connection.dataService.data_service_id
    ).subscribe({
      next: (res: ServerResponse<HeaderValues[]>)=>{
        if(res.result.length>0)this.currentHeaderValues = res.result[0];
      }
    });
  }

  onGoBacktoGlobal(){
    this.userDocument = null;
    // this.currentDate = new Date();
    this.getMetrics(this.currentDate);
    this.timeline.setDate(this.currentDate);
  }

  protected override afterFetchingContent(): void {
    super.afterFetchingContent();
    if(!this.savedDocument.model_UUID) return;
    this.getMetricTypes();
    if(this.savedDocument.model_UUID)this.showMetricsButton = true;
  }

  protected override errorReporting(title: string, message: string): void {
    this.notifyDialogService.openDialog(title, message);
  }

}
